import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { UserRole } from '@/types/roles'
import { api } from '@/lib/api'
import { normalizeWhatsAppPhone } from '@/lib/phone'
import { normalizeRoles } from '@/lib/roles'
import { derivePrimaryTeamId, normalizeTeamIds } from '@/lib/utils/teams'

// Types for authentication

export interface AdminUser {
  id: number
  username: string
  roles: UserRole[]
  // Maintain optional single-role field until all callers are migrated
  role?: UserRole
  [key: string]: unknown
  permissions?: string[]
}

export interface AgentUser {
  id: number
  username: string
  email: string
  firstName: string
  lastName: string
  roles: UserRole[]
  role?: UserRole | string
  isActive: boolean
  isOnline?: boolean
  lastSeenAt?: Date
  createdAt: Date
  updatedAt: Date
  teamIds?: number[]
  primaryTeamId?: number | null
  [key: string]: unknown
  permissions?: string[]
}

export interface PublicUser {
  id: number
  username: string
  email: string
  firstName?: string
  lastName?: string
  phone?: string
  referralCode?: string
  hasVerifiedMobile?: boolean
  referrerUsername?: string | null
  role?: UserRole | null // Optional role for regular users
  roles?: UserRole[] // Array of roles
  createdAt?: Date
  updatedAt?: Date
  [key: string]: unknown
}

function normalizeAdminUser(user: Record<string, unknown>): AdminUser {
  const roles = normalizeRoles((user as any).roles, (user as any).role)
  const roleValue = (user as any).role as UserRole | undefined
  const permissionsRaw = Array.isArray((user as any).permissions)
    ? ((user as any).permissions as string[])
    : []
  const permissions = Array.from(
    new Set(
      permissionsRaw
        .filter(code => typeof code === 'string')
        .map(code => (code as string).toUpperCase())
    )
  )
  return {
    ...user,
    roles,
    role: roleValue ?? roles[0],
    permissions,
  } as AdminUser
}

function normalizeAgentUser(user: Record<string, unknown>): AgentUser {
  const roles = normalizeRoles((user as any).roles, (user as any).role)
  const rawRole =
    ((user as any).role as UserRole | string | undefined) ?? roles[0]
  const normalizedRole =
    typeof rawRole === 'string' ? rawRole.toUpperCase() : rawRole
  const permissionsRaw = Array.isArray((user as any).permissions)
    ? ((user as any).permissions as string[])
    : []
  const permissions = Array.from(
    new Set(
      permissionsRaw
        .filter(code => typeof code === 'string')
        .map(code => (code as string).toUpperCase())
    )
  )
  const fallbackCandidate =
    (user as any)?.primaryTeamId ?? (user as any)?.teamId ?? null
  const normalizedTeamIds = normalizeTeamIds(
    (user as any)?.teamIds,
    fallbackCandidate
  )
  const primaryTeamId = derivePrimaryTeamId(
    normalizedTeamIds,
    fallbackCandidate
  )
  return {
    ...user,
    roles,
    role: normalizedRole ?? roles[0],
    teamIds: normalizedTeamIds,
    primaryTeamId,
    permissions,
    // Maintain legacy `teamId` field for components still consuming it.
    teamId: primaryTeamId,
  } as AgentUser
}

function normalizePublicUser(user: Record<string, unknown>): PublicUser {
  const roles = normalizeRoles((user as any).roles, (user as any).role)
  return {
    id: (user as any).id as number,
    username: (user as any).username as string,
    email: (user as any).email as string,
    firstName: (user as any).firstName as string | undefined,
    lastName: (user as any).lastName as string | undefined,
    phone: (user as any).phone as string | undefined,
    referralCode:
      ((user as any).referralCode as string | undefined) ??
      ((user as any).username as string),
    hasVerifiedMobile: Boolean((user as any).hasVerifiedMobile),
    referrerUsername:
      ((user as any).referrerUsername as string | null | undefined) ?? null,
    role: ((user as any).role as UserRole | null) || null, // Optional role
    roles: roles, // Array of roles
    createdAt: (user as any).createdAt as Date | undefined,
    updatedAt: (user as any).updatedAt as Date | undefined,
  } as PublicUser
}

export interface AuthState {
  // Admin authentication
  adminToken: string | null
  adminUser: AdminUser | null
  isAdminAuthenticated: boolean

  // Agent authentication
  agentToken: string | null
  agentUser: AgentUser | null
  isAgentAuthenticated: boolean

  // Public user authentication
  userToken: string | null
  publicUser: PublicUser | null
  isUserAuthenticated: boolean

  // Loading states
  isLoading: boolean

  // Actions
  setAdminAuth: (
    token: string,
    user: AdminUser | Record<string, unknown>
  ) => void
  setAgentAuth: (
    token: string,
    user: AgentUser | Record<string, unknown>
  ) => void
  setUserAuth: (
    token: string,
    user: PublicUser | Record<string, unknown>
  ) => void
  clearAdminAuth: () => void
  clearAgentAuth: () => void
  clearUserAuth: () => void
  clearAllAuth: () => void
  setLoading: (loading: boolean) => void

  // Verification methods
  verifyAdminToken: () => Promise<boolean>
  verifyAgentToken: () => Promise<boolean>
  verifyUserToken: () => Promise<boolean>

  // Login methods
  loginAdmin: (
    username: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>
  loginAgent: (
    username: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>
  signupUser: (
    username: string,
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    referrerUsername?: string
  ) => Promise<{ success: boolean; error?: string }>
  loginUser: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>

  // Logout methods
  logoutAdmin: () => Promise<void>
  logoutAgent: () => Promise<void>
  logoutUser: () => Promise<void>

  // User profile methods
  updateUserProfile: (updates: {
    username?: string
    firstName?: string
    lastName?: string
    phone?: string
  }) => Promise<{ success: boolean; error?: string }>
  changeUserPassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<{ success: boolean; error?: string }>

  // Password reset methods
  requestPasswordReset: (
    email: string
  ) => Promise<{ success: boolean; error?: string }>
  resetPassword: (
    token: string,
    newPassword: string,
    confirmPassword: string
  ) => Promise<{ success: boolean; error?: string }>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      adminToken: null,
      adminUser: null,
      isAdminAuthenticated: false,
      agentToken: null,
      agentUser: null,
      isAgentAuthenticated: false,
      userToken: null,
      publicUser: null,
      isUserAuthenticated: false,
      isLoading: true,

      // Actions
      setAdminAuth: (
        token: string,
        user: AdminUser | Record<string, unknown>
      ) => {
        const normalizedUser = normalizeAdminUser(user)
        set({
          adminToken: token,
          adminUser: normalizedUser,
          isAdminAuthenticated: true,
        })
        console.log('Admin auth set')
      },

      setAgentAuth: (
        token: string,
        user: AgentUser | Record<string, unknown>
      ) => {
        const normalizedUser = normalizeAgentUser(user)
        set({
          agentToken: token,
          agentUser: normalizedUser,
          isAgentAuthenticated: true,
        })
      },

      clearAdminAuth: () => {
        set({
          adminToken: null,
          adminUser: null,
          isAdminAuthenticated: false,
        })

        // No cookie to clear - storing token only in zustand/localStorage
      },

      clearAgentAuth: () => {
        set({
          agentToken: null,
          agentUser: null,
          isAgentAuthenticated: false,
        })
      },

      clearAllAuth: () => {
        set({
          adminToken: null,
          adminUser: null,
          isAdminAuthenticated: false,
          agentToken: null,
          agentUser: null,
          isAgentAuthenticated: false,
          userToken: null,
          publicUser: null,
          isUserAuthenticated: false,
        })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      // Verification methods
      verifyAdminToken: async (): Promise<boolean> => {
        const { adminToken } = get()
        // console.log('verifyAdminToken called, adminToken:', adminToken)

        // If the token isn't available in Zustand, do NOT attempt to read
        // a client-set cookie. The app uses client-side persistence (zustand
        // -> localStorage) and guards should rely on that. Return false if
        // the in-memory token is missing; components can rehydrate from
        // localStorage themselves (the store's onRehydrateStorage handles it).
        if (!adminToken) {
          console.log('No adminToken in store, returning false')
          return false
        }
        return true
      },

      verifyAgentToken: async (): Promise<boolean> => {
        const { agentToken } = get()
        if (!agentToken) return false

        try {
          const verifyResponse = await api.get('/agent/verify')
          console.log('verifyagenttoken')

          const statusOk =
            typeof verifyResponse?.status === 'number'
              ? verifyResponse.status >= 200 && verifyResponse.status < 300
              : true
          const isResponseSuccess =
            verifyResponse?.success === undefined
              ? statusOk
              : verifyResponse.success

          if (!isResponseSuccess) {
            console.warn('Agent verification failed:', {
              status: verifyResponse?.status,
              error: verifyResponse?.error,
              message: verifyResponse?.message,
            })
            get().clearAgentAuth()
            return false
          }

          const payload: any =
            (verifyResponse && (verifyResponse as any).data) ??
            (verifyResponse && (verifyResponse as any).user) ??
            verifyResponse

          const agentData =
            payload?.agent && typeof payload.agent === 'object'
              ? payload.agent
              : payload

          if (agentData && typeof agentData === 'object' && 'id' in agentData) {
            set({
              agentUser: normalizeAgentUser(agentData),
              isAgentAuthenticated: true,
            })
            return true
          }

          console.warn(
            'Agent verification succeeded without usable agent data',
            {
              payload,
            }
          )
          get().clearAgentAuth()
          return false
        } catch (error) {
          console.error('Agent token verification failed:', error)
          get().clearAgentAuth()
          return false
        }
      },

      // Login methods
      loginAdmin: async (username: string, password: string) => {
        set({ isLoading: true })

        try {
          const response = await api.post('/admin/login', {
            username,
            password,
          })

          console.log(
            'Admin login status:',
            response?.success !== false ? 'success' : 'failed'
          )
          const responseError =
            typeof response?.error === 'string'
              ? response.error
              : typeof response?.message === 'string'
                ? response.message
                : null
          if (response?.success === false || responseError) {
            console.error(
              '❌ Admin login failed:',
              responseError || 'Unknown error'
            )
          }
          // Api helper wraps payload under `data`; fall back to raw response for safety
          const payload = response?.data ?? response

          // Server may return token either at top-level (token/user) or in data (data.token)
          const token = payload?.token
          const user = payload?.user

          if (token) {
            // Set the token first so it's available for the verify call
            set({ adminToken: token })

            // If server already returned user, use it. Otherwise call verify endpoint to fetch user.
            if (user) {
              get().setAdminAuth(token, user)
              return { success: true }
            }

            // Verify the token to get user info
            const verifyResponse = await api.get('/admin/verify')
            const verifyPayload = verifyResponse?.data ?? verifyResponse

            if (verifyPayload?.user) {
              get().setAdminAuth(token, verifyPayload.user)
              return { success: true }
            }

            return {
              success: false,
              error: 'Unable to verify admin user after login',
            }
          }

          return {
            success: false,
            error:
              (typeof payload?.error === 'string' && payload.error) ||
              (typeof payload?.message === 'string' && payload.message) ||
              'Login failed',
          }
        } catch (error) {
          console.error('Admin login error:', error)
          // If it's an ApiError from apiFetcher, use its message; otherwise stringify
          let msg = 'Network error. Please try again.'
          if (error && typeof error === 'object') {
            const e = error as { message?: unknown }
            if (typeof e.message === 'string') {
              msg = e.message
            }
          }
          return { success: false, error: msg }
        } finally {
          set({ isLoading: false })
        }
      },

      loginAgent: async (username: string, password: string) => {
        set({ isLoading: true })

        try {
          const response = await api.post('/agent/login', {
            username,
            password,
          })
          console.log(
            'Agent login status:',
            response?.success !== false ? 'success' : 'failed'
          )
          if (response?.success === false || response?.error) {
            console.error('❌ Agent login error:', {
              success: response?.success,
              error: response?.error,
              message: response?.message,
            })
          }
          const payload: any = (response && (response as any).data) ?? response
          const token =
            payload?.token ?? payload?.data?.token ?? payload?.accessToken
          const agent = payload?.agent ?? payload?.data ?? payload
          if (token && agent) {
            console.log('agent logged in')
            get().setAgentAuth(token, agent)
            return { success: true }
          }
          console.error('unknown loginAgent error:', response)
          return {
            success: false,
            error:
              (typeof payload?.error === 'string' && payload.error) ||
              (typeof payload?.message === 'string' && payload.message) ||
              'Login failed',
          }
        } catch (error) {
          console.error('Agent login error:', error)
          return { success: false, error: 'Network error. Please try again.' }
        } finally {
          set({ isLoading: false })
        }
      },

      // Logout methods
      logoutAdmin: async () => {
        get().clearAdminAuth()
      },

      logoutAgent: async () => {
        const { agentToken } = get()

        if (agentToken) {
          try {
            await api.post('/agent/logout')
          } catch (error) {
            console.error('Agent logout error:', error)
          }
        }

        get().clearAgentAuth()

        // Clear team state to prevent showing old team data
        if (typeof window !== 'undefined') {
          const { useTeamsStore } = await import('./teams')
          useTeamsStore.getState().clearTeamState()
        }
      },

      // Public user authentication methods
      setUserAuth: (
        token: string,
        user: PublicUser | Record<string, unknown>
      ) => {
        const normalizedUser = normalizePublicUser(user)
        set({
          userToken: token,
          publicUser: normalizedUser,
          isUserAuthenticated: true,
        })
        console.log('User auth set')
      },

      clearUserAuth: () => {
        set({
          userToken: null,
          publicUser: null,
          isUserAuthenticated: false,
        })
      },

      verifyUserToken: async (): Promise<boolean> => {
        const { userToken } = get()
        if (!userToken) return false

        try {
          const response = await api.get('/users/verify')
          const payload = response?.data ?? response

          if (payload?.user) {
            get().setUserAuth(userToken, payload.user)
            return true
          }
          return false
        } catch (error) {
          console.error('User token verification failed:', error)
          get().clearUserAuth()
          return false
        }
      },

      signupUser: async (
        username: string,
        email: string,
        password: string,
        firstName?: string,
        lastName?: string,
        referrerUsername?: string
      ) => {
        set({ isLoading: true })

        try {
          const response = await api.post('/users/auth/signup', {
            username,
            email,
            password,
            firstName,
            lastName,
            ...(referrerUsername && { referrerUsername }),
          })

          const payload = response?.data ?? response

          if (payload?.success && payload?.token && payload?.user) {
            get().setUserAuth(payload.token, payload.user)
            return { success: true }
          }

          return {
            success: false,
            error: payload?.error || 'Signup failed',
          }
        } catch (error: any) {
          console.error('User signup error:', error)
          return {
            success: false,
            error:
              error?.response?.data?.error || error?.message || 'Signup failed',
          }
        } finally {
          set({ isLoading: false })
        }
      },

      loginUser: async (identifier: string, password: string) => {
        set({ isLoading: true })

        try {
          const response = await api.post('/users/auth/login', {
            identifier,
            password,
          })

          const payload = response?.data ?? response

          if (payload?.success && payload?.token && payload?.user) {
            get().setUserAuth(payload.token, payload.user)
            return { success: true }
          }

          return {
            success: false,
            error: payload?.error || 'Login failed',
          }
        } catch (error: any) {
          console.error('User login error:', error)
          return {
            success: false,
            error:
              error?.response?.data?.error || error?.message || 'Login failed',
          }
        } finally {
          set({ isLoading: false })
        }
      },

      logoutUser: async () => {
        const { userToken } = get()

        if (userToken) {
          try {
            await api.post('/users/logout')
          } catch (error) {
            console.error('User logout error:', error)
          }
        }

        get().clearUserAuth()
      },

      updateUserProfile: async (updates: {
        username?: string
        firstName?: string
        lastName?: string
        phone?: string
      }) => {
        try {
          const response = await api.put('/users/profile', {
            ...updates,
            ...(updates.phone !== undefined && {
              phone: normalizeWhatsAppPhone(updates.phone),
            }),
          })
          const payload = response?.data ?? response

          if (payload?.success && payload?.user) {
            get().setUserAuth(get().userToken!, payload.user)
            return { success: true }
          }

          return {
            success: false,
            error: payload?.error || 'Profile update failed',
          }
        } catch (error: any) {
          console.error('Update profile error:', error)
          return {
            success: false,
            error:
              error?.response?.data?.error ||
              error?.message ||
              'Profile update failed',
          }
        }
      },

      changeUserPassword: async (
        currentPassword: string,
        newPassword: string
      ) => {
        try {
          const response = await api.post('/users/change-password', {
            currentPassword,
            newPassword,
          })
          const payload = response?.data ?? response

          if (payload?.success) {
            return {
              success: true,
              // Only include error on failures
            }
          } else {
            return {
              success: false,
              error: payload?.error || 'Password change failed',
            }
          }
        } catch (error: any) {
          console.error('Change password error:', error)
          return {
            success: false,
            error:
              error?.response?.data?.error ||
              error?.message ||
              'Password change failed',
          }
        }
      },

      requestPasswordReset: async (email: string) => {
        try {
          const response = await api.post(
            '/users/auth/password/reset-request',
            {
              identifier: email, // Server expects 'identifier' not 'email'
            }
          )
          const payload = response?.data ?? response

          return {
            success: payload?.success || false,
            error: payload?.error || 'Password reset request failed',
          }
        } catch (error: any) {
          console.error('Password reset request error:', error)
          return {
            success: false,
            error:
              error?.response?.data?.error ||
              error?.message ||
              'Password reset request failed',
          }
        }
      },

      resetPassword: async (
        token: string,
        newPassword: string,
        confirmPassword: string // Client-side validation only
      ) => {
        try {
          // Client-side validation: ensure passwords match
          if (newPassword !== confirmPassword) {
            return {
              success: false,
              error: 'Passwords do not match',
            }
          }

          const response = await api.post('/users/auth/password/reset', {
            token,
            newPassword,
          })
          const payload = response?.data ?? response

          return {
            success: payload?.success || false,
            error: payload?.error || 'Password reset failed',
          }
        } catch (error: any) {
          console.error('Password reset error:', error)
          return {
            success: false,
            error:
              error?.response?.data?.error ||
              error?.message ||
              'Password reset failed',
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // When the store is rehydrated from storage (e.g. on page load), zustand
      // automatically populates the in-memory store from localStorage.
      // We only need to set isLoading: false after rehydration completes.
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.warn('Failed to rehydrate auth from storage', error)
        }
        // Use queueMicrotask to defer setState until after store initialization
        queueMicrotask(() => {
          useAuthStore.setState({ isLoading: false })
        })
      },
      partialize: state => ({
        adminToken: state.adminToken,
        adminUser: state.adminUser,
        isAdminAuthenticated: state.isAdminAuthenticated,
        agentToken: state.agentToken,
        agentUser: state.agentUser,
        isAgentAuthenticated: state.isAgentAuthenticated,
        userToken: state.userToken,
        publicUser: state.publicUser,
        isUserAuthenticated: state.isUserAuthenticated,
      }),
    }
  )
)

// Selectors for easier access (similar to Pinia getters)
export const useAdminAuth = () => {
  const store = useAuthStore()
  return {
    token: store.adminToken,
    user: store.adminUser,
    isAuthenticated: store.isAdminAuthenticated,
    isLoading: store.isLoading,
    login: store.loginAdmin,
    logout: store.logoutAdmin,
    verify: store.verifyAdminToken,
    setAuth: store.setAdminAuth,
    clearAuth: store.clearAdminAuth,
  }
}

export const useAgentAuth = () => {
  const store = useAuthStore()
  return {
    token: store.agentToken,
    user: store.agentUser,
    isAuthenticated: store.isAgentAuthenticated,
    isLoading: store.isLoading,
    login: store.loginAgent,
    logout: store.logoutAgent,
    verify: store.verifyAgentToken,
    setAuth: store.setAgentAuth,
    clearAuth: store.clearAgentAuth,
  }
}

export const usePublicUserAuth = () => {
  const store = useAuthStore()
  return {
    token: store.userToken,
    user: store.publicUser,
    isAuthenticated: store.isUserAuthenticated,
    isLoading: store.isLoading,
    signup: store.signupUser,
    login: store.loginUser,
    logout: store.logoutUser,
    verify: store.verifyUserToken,
    setAuth: store.setUserAuth,
    clearAuth: store.clearUserAuth,
    updateProfile: store.updateUserProfile,
    changePassword: store.changeUserPassword,
    requestPasswordReset: store.requestPasswordReset,
    resetPassword: store.resetPassword,
  }
}

// Immediate client-side rehydration helper
// This runs once in the browser and attempts to restore the admin/agent/user tokens
// as early as possible to avoid race conditions where components
// call verify() before the zustand store has rehydrated from localStorage.
if (typeof window !== 'undefined') {
  try {
    const raw = localStorage.getItem('auth-storage')
    if (raw) {
      const parsed = JSON.parse(raw)
      const adminToken = parsed?.state?.adminToken || parsed?.adminToken
      const agentToken = parsed?.state?.agentToken || parsed?.agentToken
      const userToken = parsed?.state?.userToken || parsed?.userToken
      const adminUser = parsed?.state?.adminUser || parsed?.adminUser
      const agentUser = parsed?.state?.agentUser || parsed?.agentUser
      const publicUser = parsed?.state?.publicUser || parsed?.publicUser

      const store = useAuthStore.getState()

      // Rehydrate admin auth if available and not already set
      if (adminToken && !store.adminToken) {
        useAuthStore.setState({
          adminToken,
          adminUser,
          isAdminAuthenticated: true,
        })
        console.log('Early rehydration: set admin token and user')
      }

      // Rehydrate agent auth if available and not already set
      if (agentToken && !store.agentToken) {
        useAuthStore.setState({
          agentToken,
          agentUser,
          isAgentAuthenticated: true,
        })
        console.log('Early rehydration: set agent token and user')
      }

      // Rehydrate public user auth if available and not already set
      if (userToken && !store.userToken) {
        useAuthStore.setState({
          userToken,
          publicUser,
          isUserAuthenticated: true,
        })
        console.log('Early rehydration: set user token and user')
      }
    }

    useAuthStore.setState({ isLoading: false })
  } catch (e) {
    // non-fatal
    console.warn('Early rehydration failed', e)
  }
}
